import { BADGE_DESCRIPTIONS } from 'features/catalog/badge-definitions';

const VALID_BADGE_IDS = new Set(BADGE_DESCRIPTIONS.keys());
const CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

function allChunksValid(chunks: string[]): boolean {
  return chunks.every((c) => VALID_BADGE_IDS.has(c));
}

/** Drop 4-char tokens that are not keys in `BADGE_DESCRIPTIONS` (retired, typos, unknown). */
function catalogBadgeIDs(chunks: string[]): string[] {
  return chunks.filter((id) => VALID_BADGE_IDS.has(id));
}

function tryRepairOffset(alphanumeric: string): string[] | null {
  const len = alphanumeric.length;
  if (len % 4 === 3) {
    for (let pos = 0; pos <= len; pos++) {
      for (const ch of CHARS) {
        const repaired = alphanumeric.slice(0, pos) + ch + alphanumeric.slice(pos);
        const chunks = repaired.match(/.{1,4}/g) || [];
        if (allChunksValid(chunks)) return chunks;
      }
    }
  }
  if (len % 4 === 1) {
    for (let pos = 0; pos < len; pos++) {
      const repaired = alphanumeric.slice(0, pos) + alphanumeric.slice(pos + 1);
      const chunks = repaired.match(/.{1,4}/g) || [];
      if (allChunksValid(chunks)) return chunks;
    }
  }
  if (len % 4 === 0) {
    const chunks = alphanumeric.match(/.{1,4}/g) || [];
    if (!allChunksValid(chunks)) {
      for (let pos = 0; pos <= len; pos++) {
        for (const ch of CHARS) {
          const repaired = alphanumeric.slice(0, pos) + ch + alphanumeric.slice(pos);
          const repChunks = repaired.match(/.{1,4}/g) || [];
          if (allChunksValid(repChunks)) return repChunks;
        }
      }
      for (let pos = 0; pos < len; pos++) {
        const repaired = alphanumeric.slice(0, pos) + alphanumeric.slice(pos + 1);
        const repChunks = repaired.match(/.{1,4}/g) || [];
        if (allChunksValid(repChunks)) return repChunks;
      }
    }
  }
  return null;
}

/**
 * Parse added badge IDs from paste code (everything after the last comma).
 * Handles spaces, zero-width chars, repairs 1-char-short strings (e.g. ibdm),
 * and repairs 1-char offset in the middle (e.g. missing "x" before 7tl).
 * Tokens that are not in `BADGE_DESCRIPTIONS` are ignored.
 */
export function parseBadgeString(raw: string): string[] {
  const cleaned = raw
    .replace(/\s+/g, '')
    .replace(/\n/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '');

  const alphanumericOnly = cleaned.replace(/[^a-z0-9]/gi, '');

  if (alphanumericOnly.length % 4 === 0) {
    const chunks = alphanumericOnly.match(/.{1,4}/g) || [];
    if (allChunksValid(chunks)) return chunks;
    const repaired = tryRepairOffset(alphanumericOnly);
    if (repaired) return repaired;
    return catalogBadgeIDs(chunks);
  }

  if (alphanumericOnly.length % 4 === 3) {
    const repaired = tryRepairOffset(alphanumericOnly);
    if (repaired) return repaired;
  }

  const truncatedLen = Math.floor(alphanumericOnly.length / 4) * 4;
  if (truncatedLen > 0) {
    const usable = alphanumericOnly.slice(0, truncatedLen);
    return catalogBadgeIDs(usable.match(/.{1,4}/g) || []);
  }

  return [];
}
